import { getHttpClient } from '@vnetwork/margarita-core/http';
import {
  COMPUTE_WORKSPACE_KEY,
  defineWorkspaceService,
  fetchSharedWorkspaces,
  normalizeId,
  readComputeWorkspace,
  sameId,
  sharedWorkspacesQuery,
  splitLoc,
  type OrgWorkspace,
  type RouteMatch,
  type SharedWorkspace,
  type WorkspaceClient,
} from '@vnetwork/margarita-shell/iam';
import { COMPUTE_NAV_GROUPS } from './nav';
import { HUB_SECTIONS } from './vpc/networking-tabs';
import { computePaths } from './paths';

/** The service type the registry and `resolveOrganizationWorkspace` know Compute by. */
export const COMPUTE_WORKSPACE_TYPE = 'compute';

interface HomeWorkspace {
  name?: string;
  organization_id: string;
  metadata_id?: string;
}

/** The shared workspaces of other organizations, as the registry lists them: `ref` is the metadata id the console URL carries. */
export function sharedWorkspaceEntries(list: readonly (Pick<SharedWorkspace, 'organization_id' | 'metadata_id'> & Partial<SharedWorkspace>)[]): OrgWorkspace[] {
  return list.map((w) => {
    const projectIds = w.project_ids ?? [];
    const resourceIds = w.machine_ids ?? [];
    return {
      serviceType: COMPUTE_WORKSPACE_TYPE,
      organizationId: normalizeId(w.organization_id),
      organizationName: w.name ?? '',
      home: false,
      ref: normalizeId(w.metadata_id),
      // Compute answers an organization-level role as a workspace with neither projects nor machines.
      scope: projectIds.length > 0 ? 'projects' : resourceIds.length > 0 ? 'resources' : 'org',
      projectIds,
      resourceIds,
    };
  });
}

/** The page slugs of the Compute sidebar: they exist in every Compute workspace. */
const COMPUTE_PAGES: readonly string[] = COMPUTE_NAV_GROUPS.flatMap((g) => g.items.map((e) => e.slug));

/** The sidebar's label of a page slug (`quotas` is `Quotas & Usage`); undefined for a slug the sidebar does not list. */
export function computePageLabel(page: string): string | undefined {
  return COMPUTE_NAV_GROUPS.flatMap((g) => g.items).find((e) => e.slug === page)?.label;
}

const VPC_HUB_TABS = HUB_SECTIONS.map((s) => s.path).filter(Boolean);

/**
 * A URL below a page's list that is about one thing: an instance, a router, a VPC, the create form of a machine. The
 * tabs of the VPC hub (`vpc/vpn`, `vpc/legacy`, ...) are pages of their own, not details.
 */
function isDetail(page: string, below: readonly string[]): boolean {
  if (below.length === 0) return false;
  return !(page === 'vpc' && below.length === 1 && VPC_HUB_TABS.includes(below[0]));
}

/** `/services/compute/<metadata id>/<page>/...` in one of the known workspaces (the search is ignored); anything else is not a Compute workspace URL. */
export function matchComputeRoute(loc: string, known: readonly OrgWorkspace[]): RouteMatch | null {
  const segments = splitLoc(loc).pathname.split('/').filter(Boolean);
  if (segments[0] !== 'services' || segments[1] !== 'compute' || !segments[2]) return null;
  const workspace = known.find((w) => w.ref && sameId(w.ref, segments[2]));
  if (!workspace) return null;
  const page = segments[3] ?? '';
  const below = segments.slice(4);
  const match: RouteMatch = { organizationId: workspace.organizationId, page, detail: isDetail(page, below) };
  // `instances/create` is a form, not a machine.
  if (page === 'instances' && below.length > 0 && below[0] !== 'create') match.resource = { serviceId: segments[2], id: below[0] };
  return match;
}

function homeEntries(raw: HomeWorkspace | null): OrgWorkspace[] {
  if (!raw) return [];
  return [
    {
      serviceType: COMPUTE_WORKSPACE_TYPE,
      organizationId: normalizeId(raw.organization_id),
      organizationName: raw.name ?? '',
      home: true,
      ref: normalizeId(raw.metadata_id),
      scope: 'org',
      projectIds: [],
      resourceIds: [],
    },
  ];
}

export function createComputeWorkspaceService(client: WorkspaceClient) {
  const sharedQuery = sharedWorkspacesQuery(() => fetchSharedWorkspaces(client));
  const sharedSource = { queryKey: sharedQuery.queryKey, staleTime: sharedQuery.staleTime, retryOnMount: sharedQuery.retryOnMount, fetch: sharedQuery.queryFn };
  return defineWorkspaceService<HomeWorkspace | null, SharedWorkspace[]>({
    type: COMPUTE_WORKSPACE_TYPE,
    home: {
      queryKey: COMPUTE_WORKSPACE_KEY,
      staleTime: 60_000,
      fetch: async () => readComputeWorkspace<HomeWorkspace>((await client.get('/workspace')).data),
    },
    // The key and the answer of `useSharedWorkspaces`: the registry and that hook share one fetch.
    shared: sharedSource,
    homeEntries,
    sharedEntries: sharedWorkspaceEntries,
    // An entry without a ref cannot be opened: the landing, never the person's own workspace.
    href: (ws, page) => (ws.ref ? (page ? `${computePaths.service(ws.ref)}/${page}` : computePaths.service(ws.ref)) : computePaths.base),
    matchRoute: matchComputeRoute,
    pages: COMPUTE_PAGES,
    dashboard: (ws) => (ws.ref ? computePaths.service(ws.ref) : computePaths.base),
    landing: computePaths.base,
    switchable: true,
    pageLabel: computePageLabel,
  });
}

// The client is looked up per call, not at import: this file is in the entry chunk (module.ts) and the registry is filled at boot.
export const workspaceService = createComputeWorkspaceService({ get: (url) => getHttpClient('computeApi').get(url) });
